/**
 * Financial Wellness metrics (spec §16): 15+ metrics computed from the
 * projection output, each a value WITH CONTEXT — no letter grades, no single
 * composite number that obscures which lever to pull.
 */
import type { Plan } from '../model/types';
import { ACCOUNT_META } from '../model/accountMeta';
import { runProjection, type ProjectionResult } from './projection';
import { contributionLimits } from './data/contributionLimits';
import { CURRENT_YEAR } from '../model/defaults';

export type MetricStatus = 'good' | 'ok' | 'attention' | 'info';

export interface WellnessMetric {
  key: string;
  label: string;
  value: string;
  /** One plain-language sentence of context. */
  detail: string;
  status: MetricStatus;
  /** What would improve it (omitted when it's already fine). */
  improve?: string;
}

const usd = (n: number) =>
  `$${Math.round(n).toLocaleString('en-US')}`;
const usdC = (n: number) =>
  Math.abs(n) >= 999_500 ? `$${(n / 1e6).toFixed(2).replace(/\.?0+$/, '')}M`
  : Math.abs(n) >= 1_000 ? `$${Math.round(n / 1000)}K` : usd(n);
const pct = (n: number) => `${Math.round(n * 100)}%`;

export interface WellnessInput {
  plan: Plan;
  projection: ProjectionResult;
  /** Ending-net-worth gain available by switching to the best claiming ages. */
  claimingDeltaNetWorth?: number;
}

export function computeWellness({ plan, projection, claimingDeltaNetWorth }: WellnessInput): WellnessMetric[] {
  const snaps = projection.snapshots;
  const first = snaps[0]!;
  const last = snaps[snaps.length - 1]!;
  const person = plan.household.people[0]!;
  const metrics: WellnessMetric[] = [];
  const push = (m: WellnessMetric) => metrics.push(m);

  const grossIncomeY1 =
    first.wagesIncome + first.selfEmploymentIncome + first.pensionIncome +
    first.annuityIncome + first.rentalRoyaltyOtherIncome + first.socialSecurityGross;
  const working = first.wagesIncome + first.selfEmploymentIncome > 0;

  // 1. Savings rate
  if (working) {
    const saved = first.employeeContributions + first.employerMatchTotal + first.surplusToSavings;
    const rate = grossIncomeY1 > 0 ? saved / grossIncomeY1 : 0;
    push({
      key: 'savings_rate', label: 'Savings rate',
      value: pct(rate),
      detail: `${usdC(saved)} of ${usdC(grossIncomeY1)} income saved this year (incl. employer match).`,
      status: rate >= 0.15 ? 'good' : rate >= 0.08 ? 'ok' : 'attention',
      improve: rate < 0.15 ? '15%+ of gross income is the common benchmark while working.' : undefined,
    });
  }

  // 2. Contribution vs. the annual limit
  if (working) {
    const L = contributionLimits(CURRENT_YEAR);
    const age = CURRENT_YEAR - person.birthYear;
    const catchUp = age >= 60 && age <= 63 ? L.employer.superCatchUp60to63 : age >= 50 ? L.employer.catchUp50 : 0;
    const limit = L.employer.electiveDeferral + catchUp;
    const contrib = first.employeeContributions;
    push({
      key: 'contribution_room', label: 'Retirement contributions vs. the limit',
      value: `${usdC(contrib)} of ${usdC(limit)}`,
      detail: contrib >= limit - 500
        ? 'You are using essentially all of the tax-advantaged space available.'
        : `${usdC(limit - contrib)} of tax-advantaged space is going unused this year.`,
      status: contrib >= limit - 500 ? 'good' : contrib >= limit * 0.5 ? 'ok' : 'attention',
      improve: contrib < limit - 500 ? 'Raise payroll deferrals if cash flow allows — the space does not roll over.' : undefined,
    });
  }

  // 3. Employer match captured. The figure comes from the engine, which
  // measures each person's contribution against THEIR OWN wages — a second
  // earner's salary never counts against the first one's match threshold.
  const matchAccounts = plan.accounts.filter((a) => a.contributions?.employerMatch);
  if (matchAccounts.length === 0 && working) {
    push({
      key: 'employer_match', label: 'Employer match',
      value: 'None entered',
      detail: 'No employer match is set up on any account — if your plan offers one, add it under Accounts; it changes the math.',
      status: 'info',
    });
  } else if (matchAccounts.length > 0 && working) {
    const leftOnTable = first.employerMatchUnclaimed;
    push({
      key: 'employer_match', label: 'Employer match captured',
      value: leftOnTable < 100 ? 'All of it' : `${usdC(leftOnTable)}/yr left on the table`,
      detail: leftOnTable < 100
        ? `Collecting ${usdC(first.employerMatchTotal)} of match this year — contributions reach the threshold.`
        : 'Contributions are below the match threshold — this is a 50-100% instant return being declined.',
      status: leftOnTable < 100 ? 'good' : 'attention',
      improve: leftOnTable >= 100 ? 'Contribute at least to the match threshold before anything else.' : undefined,
    });
  }

  // 4. Withdrawal rate at retirement
  const retYear = person.birthYear + person.retirementAge;
  const retSnap = snaps.find((s) => s.year === Math.max(retYear, CURRENT_YEAR));
  if (retSnap) {
    const portfolio = retSnap.accounts.reduce((t, f) => t + f.startBalance, 0);
    const draw = retSnap.totalWithdrawals + retSnap.totalRmd;
    const rate = portfolio > 0 ? draw / portfolio : 0;
    push({
      key: 'withdrawal_rate', label: 'Withdrawal rate at retirement',
      value: pct(rate),
      detail: `${usdC(draw)} drawn from a ${usdC(portfolio)} portfolio in the first retirement year.`,
      status: rate <= 0.045 ? 'good' : rate <= 0.06 ? 'ok' : 'attention',
      improve: rate > 0.045 ? 'Above ~4-5%, plans lean heavily on good markets. More savings, later retirement, or lower spending each pull this down.' : undefined,
    });
  }

  // 5. Emergency fund. A designated safety bucket counts whatever it is held
  // in — sheltering spending from bad markets is the same job an emergency
  // fund does, and its balance is deliberately kept liquid.
  const bucketId = plan.decisions.bucketStrategy?.accountId;
  const bucket = bucketId ? plan.accounts.find((a) => a.id === bucketId) : undefined;
  const cashOnly = plan.accounts
    .filter((a) => a.id !== bucketId && (a.type === 'checking_savings' || a.type === 'cd_money_market'))
    .reduce((t, a) => t + a.balance, 0);
  const bucketBalance = bucket?.balance ?? 0;
  const cashBalance = cashOnly + bucketBalance;
  const monthlyMustSpend =
    (first.essentialSpending + first.healthcarePremiums + first.healthcareOOP + first.debtService) / 12;
  const months = monthlyMustSpend > 0 ? cashBalance / monthlyMustSpend : 12;
  push({
    key: 'emergency_fund', label: 'Emergency fund',
    value: `${months.toFixed(1)} months`,
    detail: bucketBalance > 0
      ? `${usdC(cashBalance)} liquid — ${usdC(cashOnly)} in cash plus the ${usdC(bucketBalance)} safety bucket — against ${usdC(monthlyMustSpend)}/month of must-spend.`
      : `${usdC(cashBalance)} in cash against ${usdC(monthlyMustSpend)}/month of must-spend.`,
    status: months >= 6 ? 'good' : months >= 3 ? 'ok' : 'attention',
    improve: months < 3 ? 'Three months of essentials is the floor; six is comfortable.' : undefined,
  });

  // 6. Debt-to-income + 7. Non-mortgage debt
  const totalDebt = plan.debts.reduce((t, d) => t + d.balance, 0);
  const nonMortgage = plan.debts
    .filter((d) => d.type !== 'mortgage' && d.type !== 'heloc')
    .reduce((t, d) => t + d.balance, 0);
  if (totalDebt > 0 && grossIncomeY1 > 0) {
    const dti = totalDebt / grossIncomeY1;
    push({
      key: 'debt_to_income', label: 'Debt-to-income',
      value: `${dti.toFixed(1)}×`,
      detail: `${usdC(totalDebt)} of debt against ${usdC(grossIncomeY1)} of annual income.`,
      status: dti <= 2 ? 'good' : dti <= 3.5 ? 'ok' : 'attention',
    });
  } else {
    push({
      key: 'debt_to_income', label: 'Debt-to-income',
      value: totalDebt === 0 ? 'Debt-free' : '—',
      detail: totalDebt === 0
        ? 'No debts entered — nothing is competing with savings for cash flow.'
        : 'No current income to compare against.',
      status: totalDebt === 0 ? 'good' : 'info',
    });
  }
  push({
    key: 'non_mortgage_debt', label: 'Non-mortgage debt',
    value: nonMortgage === 0 ? 'None' : usdC(nonMortgage),
    detail: nonMortgage === 0
      ? 'No consumer debt — mortgages aside, growth compounds for you, not a lender.'
      : 'Consumer debt usually carries rates no portfolio reliably beats.',
    status: nonMortgage === 0 ? 'good' : nonMortgage < 20_000 ? 'ok' : 'attention',
    improve: nonMortgage > 0 ? 'Paying this down is a guaranteed return at the loan rate.' : undefined,
  });

  // 8. Tax diversification
  const fam = { pretax: 0, roth: 0, aftertax: 0 };
  for (const a of plan.accounts) {
    const f = ACCOUNT_META[a.type].taxFamily;
    if (f === 'pretax') fam.pretax += a.balance;
    else if (f === 'roth') fam.roth += a.balance;
    else if (f === 'aftertax') fam.aftertax += a.balance;
  }
  const famTotal = fam.pretax + fam.roth + fam.aftertax;
  if (famTotal > 0) {
    const shares = {
      pretax: fam.pretax / famTotal, roth: fam.roth / famTotal, aftertax: fam.aftertax / famTotal,
    };
    const maxShare = Math.max(shares.pretax, shares.roth, shares.aftertax);
    push({
      key: 'tax_diversification', label: 'Tax diversification',
      value: `${pct(shares.pretax)} pre-tax · ${pct(shares.roth)} Roth · ${pct(shares.aftertax)} after-tax`,
      detail: maxShare > 0.8
        ? 'Heavily concentrated in one tax treatment — future tax-law changes hit with full force.'
        : 'A mix of tax treatments gives room to steer income in retirement.',
      status: maxShare > 0.8 ? 'attention' : maxShare > 0.65 ? 'ok' : 'good',
      improve: shares.pretax > 0.7 ? 'Roth conversions in low-income years rebalance this — see the explorer.' : undefined,
    });
  }

  // 9. Guaranteed income vs. must-spend
  const retSnaps = snaps.filter(
    (s) => s.wagesIncome + s.selfEmploymentIncome === 0 && s.essentialSpending > 0,
  );
  if (retSnaps.length > 0) {
    const cover =
      retSnaps.reduce((t, s) => {
        const must = s.essentialSpending + s.healthcarePremiums + s.healthcareOOP + s.ltcNetCost;
        return t + Math.min(1, must > 0 ? s.guaranteedIncome / must : 1);
      }, 0) / retSnaps.length;
    push({
      key: 'guaranteed_cover', label: 'Guaranteed income covers must-spend',
      value: pct(cover),
      detail: 'Social Security, pensions, and annuities against essentials — the rest is on the portfolio, every year, no matter what markets do.',
      status: cover >= 0.8 ? 'good' : cover >= 0.5 ? 'ok' : 'attention',
      improve: cover < 0.8 ? 'Delaying Social Security raises the guaranteed floor for life.' : undefined,
    });
  }

  // 10. Survives all three worldviews
  let survived = 0;
  const labels: string[] = [];
  for (const prof of plan.assumptionProfiles) {
    const v: Plan = structuredClone(plan);
    v.activeAssumptionLabel = prof.label;
    const r = runProjection(v, CURRENT_YEAR);
    if (r.firstDepletionYear === undefined) survived += 1;
    else labels.push(`${prof.label}: runs out ${r.firstDepletionYear}`);
  }
  push({
    key: 'profiles_survived', label: 'Survives the three worldviews',
    value: `${survived} of ${plan.assumptionProfiles.length}`,
    detail: survived === plan.assumptionProfiles.length
      ? 'The plan holds under optimistic, average, and pessimistic assumptions alike.'
      : labels.join(' · '),
    status: survived === plan.assumptionProfiles.length ? 'good' : survived >= 2 ? 'ok' : 'attention',
    improve: survived < plan.assumptionProfiles.length ? 'A plan that only works when markets cooperate is a hope, not a plan.' : undefined,
  });

  // 11. Portfolio longevity
  push({
    key: 'longevity', label: 'Portfolio longevity',
    value: projection.firstDepletionYear ? `runs out ${projection.firstDepletionYear}` : `lasts to ${last.year}`,
    detail: projection.firstDepletionYear
      ? `Money runs out at age ${projection.firstDepletionYear - person.birthYear} under the active assumptions.`
      : 'Money lasts through the whole plan under the active assumptions.',
    status: projection.firstDepletionYear ? 'attention' : 'good',
  });

  // 12. Effective lifetime tax rate
  const lifetimeTax = snaps.reduce((t, s) => t + s.totalTaxLiability, 0);
  const lifetimeIncome = snaps.reduce(
    (t, s) => t + s.wagesIncome + s.selfEmploymentIncome + s.pensionIncome + s.annuityIncome +
      s.rentalRoyaltyOtherIncome + s.socialSecurityGross + s.totalWithdrawals, 0,
  );
  push({
    key: 'lifetime_tax_rate', label: 'Effective lifetime tax rate',
    value: pct(lifetimeIncome > 0 ? lifetimeTax / lifetimeIncome : 0),
    detail: `${usdC(lifetimeTax)} of tax over the plan. Claiming timing, withdrawal order, and Roth conversions are the levers.`,
    status: 'info',
  });

  // 13. Healthcare gap before 65
  const gapYears = snaps.filter((s) => s.acaFplRatio != null).length;
  if (gapYears > 0) {
    const gapCost = snaps.filter((s) => s.acaFplRatio != null)
      .reduce((t, s) => t + s.healthcarePremiums, 0);
    push({
      key: 'healthcare_gap', label: 'Healthcare before Medicare',
      value: `${gapYears} years to bridge`,
      detail: `${usdC(gapCost)} of projected marketplace premiums between retirement and 65.`,
      status: gapCost > 100_000 ? 'attention' : 'ok',
      improve: 'Keeping taxable income below 400% of the poverty level in those years preserves the subsidy.',
    });
  } else {
    push({
      key: 'healthcare_gap', label: 'Healthcare before Medicare',
      value: 'No gap',
      detail: 'Retirement lands at 65 or later (or coverage continues), so Medicare picks up immediately.',
      status: 'good',
    });
  }

  // 14. Long-term care exposure
  const ltcModeled = (plan.ltcEvents ?? []).some((e) => e.enabled);
  push({
    key: 'ltc', label: 'Long-term care',
    value: ltcModeled ? 'Modeled' : 'Not modeled',
    detail: ltcModeled
      ? 'A care event is in the projection — you have seen what it does to the plan.'
      : 'Roughly half of retirees need some paid care; the plan currently assumes zero.',
    status: ltcModeled ? 'good' : 'attention',
    improve: !ltcModeled ? 'Toggle a care event in Healthcare to see the exposure before deciding to self-insure.' : undefined,
  });

  // 15. Estate at longevity
  push({
    key: 'estate', label: 'Estate value at the end',
    value: usdC(Math.max(0, last.netWorth / last.cumulativeInflationFactor)),
    detail: "Today's dollars, before any heir taxes on pre-tax accounts.",
    status: 'info',
  });

  // 16. Housing cost share
  const housingCost = first.propertyCosts +
    plan.debts.filter((d) => d.type === 'mortgage' || d.type === 'heloc')
      .reduce((t, d) => t + Math.min(d.monthlyPayment * 12, d.balance), 0);
  if (housingCost > 0 && first.totalOutflow > 0) {
    const share = housingCost / first.totalOutflow;
    push({
      key: 'housing_share', label: 'Housing share of spending',
      value: pct(share),
      detail: `${usdC(housingCost)} of ${usdC(first.totalOutflow)} annual outflow goes to housing.`,
      status: share <= 0.3 ? 'good' : share <= 0.4 ? 'ok' : 'attention',
      improve: share > 0.4 ? 'Downsizing is a first-class action in Property — worth a scenario.' : undefined,
    });
  } else {
    push({
      key: 'housing_share', label: 'Housing share of spending',
      value: 'Not itemized',
      detail: 'No property or mortgage is entered separately — housing is inside the general budget.',
      status: 'info',
    });
  }

  // 17. Social Security claiming efficiency
  if (claimingDeltaNetWorth != null) {
    push({
      key: 'claiming_efficiency', label: 'Social Security claiming vs. optimal',
      value: claimingDeltaNetWorth < 5_000 ? 'At or near optimal' : `${usdC(claimingDeltaNetWorth)} available`,
      detail: claimingDeltaNetWorth < 5_000
        ? 'Your claiming ages are within noise of the best combination found.'
        : 'A different claiming-age combination ends the plan with this much more — see the heat map.',
      status: claimingDeltaNetWorth < 5_000 ? 'good' : claimingDeltaNetWorth < 25_000 ? 'ok' : 'attention',
      improve: claimingDeltaNetWorth >= 5_000 ? 'One click in the claiming explorer applies the better ages.' : undefined,
    });
  }

  return metrics;
}
